/**
 * AdminMessagingPage — super-admin editor for the Mission Control
 * messaging layer.
 *
 * Why this page exists
 * ────────────────────
 * The brand brief lives in `config/messaging.json` (the defaults
 * tier). But positioning evolves: a sales meeting reveals a sharper
 * line, a customer hates a phrase, the CMO wants to A/B test a
 * tagline. Rather than waiting for a code deploy, super-admins
 * change the live copy here. Overrides land in Mongo
 * (`messaging_overrides` singleton) and the SPA picks them up on
 * the next page load.
 *
 * Resolution model
 * ────────────────
 *   override (this page) > default (messaging.json) > caller fallback
 *
 * UX
 * ──
 *  • One row per leaf path. Each row shows the dot-path, the default
 *    (read-only), and the override (editable inline). Long values
 *    auto-promote to a textarea.
 *  • Search filters by path OR value substring.
 *  • Clear-one (row trash icon) deletes a single override; clear-all
 *    (header trash) nukes the whole map.
 *  • Status pill in the header: "{N} overrides live".
 *  • Save/Cancel per row. Saves are atomic — one PUT per row.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MessageSquare,
  Save,
  Trash2,
  X as XIcon,
  Search,
  Loader2,
  CheckCircle2,
  RotateCcw,
  Edit3,
  Info,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { getLightSectionTheme } from '../../lib/sectionThemes';
import { applyOverridesLocally } from '../../lib/messagingClient';
import defaults from '../../config/messaging.json';

// ── helpers ────────────────────────────────────────────────────
/**
 * Flatten a nested object to `[{ path: 'a.b.c', value: '…' }]`.
 * Skips paths that begin with `_` (those are metadata, not copy).
 */
function flattenLeaves(obj, prefix = '') {
  const out = [];
  if (obj == null || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj)) {
    if (k.startsWith('_')) continue;
    const path = prefix ? `${prefix}.${k}` : k;
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      out.push(...flattenLeaves(v, path));
    } else if (typeof v === 'string' || typeof v === 'number') {
      out.push({ path, value: String(v) });
    }
    // Arrays / non-string scalars are intentionally not surfaced here —
    // the brief only governs prose copy; structured data shouldn't be
    // editable through this UI.
  }
  return out;
}

// Pre-flatten defaults once — they don't change at runtime.
const DEFAULT_ROWS = flattenLeaves(defaults);

// ── component ──────────────────────────────────────────────────
export default function AdminMessagingPage() {
  const sectionTheme = getLightSectionTheme();

  const [overrides, setOverrides] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingPath, setEditingPath] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [savingPath, setSavingPath] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'overridden' | 'default'
  const iframeRef = useRef(null);
  // Initial preview src — captured ONCE per edit-session so changing the
  // draft value doesn't reload the iframe. Subsequent updates land via
  // postMessage (see effect below).
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewReady, setPreviewReady] = useState(false);

  // ── Live preview channel ────────────────────────────────────
  // When the editor draft changes, postMessage the merged override
  // set to the iframe so it re-renders without a reload. Debounced
  // so we don't spam the child on every keystroke.
  useEffect(() => {
    if (!editingPath || !iframeRef.current) return undefined;
    const handle = setTimeout(() => {
      const merged = { ...overrides, [editingPath]: draftValue };
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'mc_messaging_preview', overrides: merged },
          window.location.origin,
        );
      } catch {
        // postMessage can throw if the iframe is mid-navigation;
        // safe to swallow — the next debounce tick will retry.
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [editingPath, draftValue, overrides]);

  const loadOverrides = useCallback(async () => {
    try {
      const res = await api.get('/mc/messaging/resolved');
      setOverrides(res?.data?.overrides || {});
      setUpdatedAt(res?.data?.updated_at || null);
    } catch (err) {
      toast.error('Could not load messaging overrides — are you signed in as super-admin?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  // Merge defaults with current overrides for display.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return DEFAULT_ROWS.filter((r) => {
      const isOverridden = Object.prototype.hasOwnProperty.call(overrides, r.path);
      if (filter === 'overridden' && !isOverridden) return false;
      if (filter === 'default' && isOverridden) return false;
      if (!q) return true;
      return r.path.toLowerCase().includes(q)
        || r.value.toLowerCase().includes(q)
        || (overrides[r.path] || '').toLowerCase().includes(q);
    }).map((r) => ({
      ...r,
      overrideValue: overrides[r.path] ?? null,
      isOverridden: Object.prototype.hasOwnProperty.call(overrides, r.path),
    }));
  }, [search, filter, overrides]);

  const overrideCount = Object.keys(overrides).length;

  const startEdit = (row) => {
    setEditingPath(row.path);
    const initialDraft = row.overrideValue ?? row.value;
    setDraftValue(initialDraft);
    // Iframe loads the actual home page at `/`; the parent (this page)
    // pushes the draft overrides in via postMessage on every keystroke.
    // We INTENTIONALLY don't bake the draft into the iframe URL — that
    // path was fragile because React Router's client-side navigation
    // doesn't always re-run module-init code with fresh URL params.
    setPreviewSrc('/');
    setPreviewReady(false);
  };

  const cancelEdit = () => {
    setEditingPath(null);
    setDraftValue('');
    setPreviewSrc(null);
    setPreviewReady(false);
  };

  const saveEdit = async (row) => {
    const trimmed = draftValue;
    setSavingPath(row.path);
    try {
      const res = await api.put('/mc/messaging/override', {
        path: row.path,
        // If the user typed the default verbatim, save it as an override
        // anyway — that's still their explicit intent and avoids surprise
        // diffs when defaults later change.
        value: trimmed,
      });
      const next = res?.data?.overrides || {};
      setOverrides(next);
      setUpdatedAt(res?.data?.updated_at || null);
      applyOverridesLocally(next); // refresh in-memory cache so the rest
                                    // of the app picks it up immediately
                                    // without a page reload
      toast.success('Override saved · live everywhere');
      setEditingPath(null);
      setPreviewSrc(null);
      setPreviewReady(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Save failed';
      toast.error(detail);
    } finally {
      setSavingPath(null);
    }
  };

  const clearOne = async (row) => {
    if (!window.confirm(`Reset "${row.path}" to the default value?`)) return;
    setSavingPath(row.path);
    try {
      const res = await api.put('/mc/messaging/override', {
        path: row.path,
        value: '', // empty clears
      });
      const next = res?.data?.overrides || {};
      setOverrides(next);
      setUpdatedAt(res?.data?.updated_at || null);
      applyOverridesLocally(next);
      toast.success(`"${row.path}" reset to default`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Reset failed';
      toast.error(detail);
    } finally {
      setSavingPath(null);
    }
  };

  const clearAll = async () => {
    if (overrideCount === 0) return;
    if (!window.confirm(
      `Reset ALL ${overrideCount} override(s) to defaults? This restores the brand brief baseline everywhere in the app.`,
    )) return;
    try {
      const res = await api.delete('/mc/messaging/override');
      const next = res?.data?.overrides || {};
      setOverrides(next);
      setUpdatedAt(res?.data?.updated_at || null);
      applyOverridesLocally(next);
      toast.success('All overrides cleared — brand brief defaults restored');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Reset failed';
      toast.error(detail);
    }
  };

  const hero = (
    <PageHero
      title="Messaging Layer"
      description="Live editor for every piece of user-facing copy in Mission Control. Defaults are shipped via the brand brief (config/messaging.json); your overrides here layer on top and take effect immediately for every user."
      theme={sectionTheme}
      testId="admin-messaging-hero"
      actions={(
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="text-xs"
            data-testid="messaging-override-count"
          >
            {overrideCount} override{overrideCount === 1 ? '' : 's'} live
          </Badge>
          {overrideCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              className="text-xs"
              data-testid="messaging-clear-all-btn"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset all to defaults
            </Button>
          )}
        </div>
      )}
    />
  );

  return (
    <PageShell theme={sectionTheme} hero={hero}>
      <div className="space-y-4" data-testid="admin-messaging-page">

        {/* Top toolbar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by path or value…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="messaging-search-input"
            />
          </div>
          <div className="flex items-center gap-1 text-xs bg-slate-50 border border-slate-200 rounded-md p-0.5">
            {[
              { id: 'all', label: 'All' },
              { id: 'overridden', label: `Overridden (${overrideCount})` },
              { id: 'default', label: `Default (${DEFAULT_ROWS.length - overrideCount})` },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilter(opt.id)}
                className={`px-2.5 py-1 rounded transition-colors ${
                  filter === opt.id
                    ? 'bg-white shadow-sm text-slate-900 font-semibold border border-slate-200'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`messaging-filter-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Helper banner */}
        <div className="flex items-start gap-2 text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="messaging-help-banner">
          <Info className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="leading-relaxed">
            <strong className="text-blue-900">How this works:</strong> Each row shows the
            shipped <em>default</em> from <code className="bg-white px-1 rounded text-[11px]">config/messaging.json</code>
            {' '}alongside any live <em>override</em>. Saving a row updates Mongo and the SPA
            picks it up instantly — no deploy required. Clearing an override falls back to
            the default. {updatedAt && (
              <span className="text-blue-700"> · Last updated {new Date(updatedAt).toLocaleString()}.</span>
            )}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500" data-testid="messaging-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading messaging layer…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center text-slate-500 py-16" data-testid="messaging-empty">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p>No paths match your search.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="messaging-rows">
            {rows.map((row) => {
              const isEditing = editingPath === row.path;
              const isSaving = savingPath === row.path;
              const liveValue = row.overrideValue ?? row.value;
              const isLong = liveValue.length > 80;
              return (
                <div
                  key={row.path}
                  className={`rounded-md border p-3 transition-colors ${
                    row.isOverridden
                      ? 'border-emerald-200 bg-emerald-50/40'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`messaging-row-${row.path}`}
                >
                  {/* Path + override badge */}
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <code className="text-[11px] font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
                      {row.path}
                    </code>
                    <div className="flex items-center gap-1.5">
                      {row.isOverridden && (
                        <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">
                          <CheckCircle2 className="h-3 w-3 mr-0.5" />
                          Overridden
                        </Badge>
                      )}
                      {!isEditing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => startEdit(row)}
                          data-testid={`messaging-edit-${row.path}`}
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      )}
                      {row.isOverridden && !isEditing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => clearOne(row)}
                          disabled={isSaving}
                          data-testid={`messaging-clear-${row.path}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Reset
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Default + override side-by-side */}
                  {!isEditing ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">
                          Default (brand brief)
                        </p>
                        <p className="text-sm text-slate-700 leading-snug" data-testid={`messaging-default-${row.path}`}>
                          {row.value}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">
                          {row.isOverridden ? 'Override (live)' : 'Override'}
                        </p>
                        <p
                          className={`text-sm leading-snug ${
                            row.isOverridden ? 'text-emerald-800 font-medium' : 'text-slate-400 italic'
                          }`}
                          data-testid={`messaging-override-${row.path}`}
                        >
                          {row.isOverridden ? row.overrideValue : '— (falls through to default)'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                        Default — for reference
                      </p>
                      <p className="text-xs text-slate-500 italic border-l-2 border-slate-200 pl-2 mb-1.5">
                        {row.value}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">
                        New override
                      </p>
                      {isLong ? (
                        <Textarea
                          value={draftValue}
                          onChange={(e) => setDraftValue(e.target.value)}
                          className="text-sm min-h-[80px]"
                          autoFocus
                          data-testid={`messaging-textarea-${row.path}`}
                        />
                      ) : (
                        <Input
                          type="text"
                          value={draftValue}
                          onChange={(e) => setDraftValue(e.target.value)}
                          className="text-sm"
                          autoFocus
                          data-testid={`messaging-input-${row.path}`}
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveEdit(row)}
                          disabled={isSaving || draftValue === (row.overrideValue ?? row.value)}
                          data-testid={`messaging-save-${row.path}`}
                        >
                          {isSaving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <Save className="h-3.5 w-3.5 mr-1" />
                          )}
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEdit}
                          disabled={isSaving}
                          data-testid={`messaging-cancel-${row.path}`}
                        >
                          <XIcon className="h-3.5 w-3.5 mr-1" />
                          Cancel
                        </Button>
                        <span className="text-[11px] text-slate-500 ml-2">
                          {draftValue.length} chars
                        </span>
                      </div>

                      {/* Live preview — iframe loads /?mc_preview_overrides=…
                          ONCE per edit session; subsequent draftValue
                          changes flow via postMessage so we never reload
                          the child page on every keystroke. */}
                      {previewSrc && (
                        <div className="mt-3 border border-slate-200 rounded-md overflow-hidden bg-slate-50" data-testid={`messaging-preview-${row.path}`}>
                          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white">
                            <div className="flex items-center gap-2">
                              <div className={`h-2 w-2 rounded-full ${previewReady ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                                Live preview {previewReady ? '· in sync' : '· loading…'}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                — what users will see after you save
                              </span>
                            </div>
                            <a
                              href={previewSrc}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 transition-colors"
                              data-testid={`messaging-preview-open-new-${row.path}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                              Open full-size
                            </a>
                          </div>
                          {/* Unsaved-changes banner — this iframe always
                              renders the in-progress DRAFT, never the live
                              saved copy, so make that unmistakable. */}
                          {draftValue !== (row.overrideValue ?? row.value) && (
                            <div
                              className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-amber-800"
                              data-testid={`messaging-preview-unsaved-${row.path}`}
                            >
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                              <span className="text-[11px] font-semibold uppercase tracking-wider">
                                Preview — not saved yet
                              </span>
                              <span className="text-[10px] text-amber-600/90">
                                Click “Save” to make these changes live for everyone.
                              </span>
                            </div>
                          )}
                          <div className="relative bg-white">
                            <iframe
                              ref={iframeRef}
                              src={previewSrc}
                              title="Live preview"
                              className="w-full h-[480px] border-0 block"
                              onLoad={() => {
                                setPreviewReady(true);
                                // Push the current draft into the iframe
                                // the moment it finishes loading. Without
                                // this, the iframe would render defaults
                                // until the next keystroke triggers the
                                // debounced effect.
                                try {
                                  const merged = { ...overrides, [row.path]: draftValue };
                                  iframeRef.current?.contentWindow?.postMessage(
                                    { type: 'mc_messaging_preview', overrides: merged },
                                    window.location.origin,
                                  );
                                } catch { /* iframe gone */ }
                              }}
                              data-testid={`messaging-preview-iframe-${row.path}`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer info */}
        <div className="text-[11px] text-slate-400 pt-4 border-t border-slate-100">
          {DEFAULT_ROWS.length} editable paths · sourced from <code className="bg-slate-50 px-1 rounded">config/messaging.json</code> v{defaults?._meta?.version || '?'}.
          Need to change a default that ships with the brand brief? Edit the JSON file in a PR.
        </div>
      </div>
    </PageShell>
  );
}
